import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { useDictText } from '../../components/DictSelect';
import CheckPickList from '../../components/CheckPickList';
import SearchSelect from '../../components/SearchSelect';
import {
    listParams,
    ListParamsParams,
    MaterialKeyKind,
    ParamRow,
    ProcessArch,
} from '../../api/parameter';
import { Device, deviceLabel } from '../../api/device';
import { DictEntry } from '../../api/dict';
import { ApiError } from '../../api/request';
import { downloadCsv, dateSuffix } from '../../utils/exportCsv';
import { formatTime } from '../../utils/time';
import { STATUS_OPTS } from './paramArchSchemas';

const STATUS_SELECT_OPTS = STATUS_OPTS.map((s) => ({
    value: s.v,
    label: s.label,
}));
import { FieldSpec, parseFields } from './SchemaEditModal';
import {
    fetchSchemaFieldsCached,
    fieldDisplayName,
    toFieldNameMap,
} from '../../api/schemaField';

// 参数总览导出弹窗 (两步): 先按筛选条件查出命中行, 再勾选要导出的行 → CSV.
// 关键列固定 (材料键随框架分化), 参数值按各行 fields 定义拍平成动态列 —
// 列顺序: 首个出现的行的字段顺序优先, 其余按出现先后追加; 表头用 label(unit).

export interface ParamExportFilter {
    keyword: string;
    family: string;
    deviceKey: string;
    materialKey: string;
    substrateXcode: string;
    subProcess: string;
    processingMode: string;
    resultTag: string;
    status: string;
}

const STATUS_TEXT: Record<string, string> = Object.fromEntries(
    STATUS_OPTS.map((s) => [s.v, s.label]),
);

function ParamExportModal({
    open,
    archCode,
    arch,
    devices,
    subProcessOpts,
    modeOpts,
    preset,
    onClose,
}: {
    open: boolean;
    archCode: string;
    arch?: ProcessArch;
    devices: Device[];
    subProcessOpts: DictEntry[];
    /** 加工模式下拉候选 (列表页按当前设备算好传入) */
    modeOpts: { value: string; label: string }[];
    /** 打开时预填的筛选 (取自列表页当前条件) */
    preset: ParamExportFilter;
    onClose: () => void;
}) {
    const [f, setF] = useState<ParamExportFilter>(preset);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState('');
    const [error, setError] = useState('');
    /** null = 还没查询; 非 null = 命中行清单 (供勾选) */
    const [rows, setRows] = useState<ParamRow[] | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    // 加工类型 code → 字典 label (预览/搜索/CSV 统一走 label, 不裸显 code)
    const subProcLabel = (code: string) =>
        subProcessOpts.find((o) => o.code === code)?.label || code;

    useEffect(() => {
        if (open) {
            setF(preset);
            setError('');
            setProgress('');
            setRows(null);
            setSelected(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 改动筛选条件后旧结果作废, 必须重新查询, 避免导出与筛选条不一致
    const patch = (p: Partial<ParamExportFilter>) => {
        setF((prev) => ({ ...prev, ...p }));
        setRows(null);
        setSelected(new Set());
        setError('');
    };

    const keyKind: MaterialKeyKind = arch?.materialKeyKind || 'xcode';
    const families = Array.from(
        new Set(devices.map((d) => d.family).filter(Boolean)),
    );

    const materialCell = (r: ParamRow): string[] => {
        if (keyKind === 'dual_xcode')
            return [r.filmXcode || '', r.substrateXcode || ''];
        if (keyKind === 'category_code')
            return [r.type1 || '', r.type2 || ''];
        return [r.xcode || ''];
    };
    const materialHeaders =
        keyKind === 'dual_xcode'
            ? ['热压膜', '基材']
            : keyKind === 'category_code'
              ? ['一级分类', '二级分类']
              : ['材质编码'];

    // 第一步: 按筛选翻页拉全量命中行, 默认全部勾上
    const onQuery = async () => {
        setBusy(true);
        setError('');
        try {
            const q: ListParamsParams = {
                keyword: f.keyword.trim() || undefined,
                deviceKey: f.deviceKey || undefined,
                family: !f.deviceKey ? f.family || undefined : undefined,
                materialKeyLike: f.materialKey.trim() || undefined,
                substrateXcode:
                    keyKind === 'dual_xcode'
                        ? f.substrateXcode.trim() || undefined
                        : undefined,
                subProcess: f.subProcess || undefined,
                processingMode: f.processingMode || undefined,
                resultTag: f.resultTag.trim() || undefined,
                status: f.status || undefined,
            };
            const all: ParamRow[] = [];
            let page = 1;
            for (;;) {
                const res = await listParams(archCode, {
                    ...q,
                    page,
                    pageSize: 200,
                });
                const batch = res.list || [];
                all.push(...batch);
                setProgress(`已拉取 ${all.length} / ${res.totalCount} 行`);
                if (batch.length === 0 || all.length >= res.totalCount) break;
                page += 1;
            }
            setRows(all);
            setSelected(new Set(all.map((r) => String(r.id))));
            if (all.length === 0) setError('当前筛选条件下没有数据');
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setBusy(false);
            setProgress('');
        }
    };

    // 第二步: 把勾选的行拍平成 CSV 下载
    const onExport = async () => {
        const all = (rows || []).filter((r) => selected.has(String(r.id)));
        if (all.length === 0) return;
        setBusy(true);
        setError('');
        try {
            // 动态参数列: 按各行 fields 定义收集 (保序去重),
            // 表头 名称(unit) — 行级 label > 字典字段名称 > key
            const dictNames = toFieldNameMap(
                await fetchSchemaFieldsCached().catch(() => []),
            );
            const colOrder: string[] = [];
            const colLabel = new Map<string, string>();
            for (const r of all) {
                for (const fd of parseFields(r.fields) as FieldSpec[]) {
                    if (!colLabel.has(fd.k)) {
                        colOrder.push(fd.k);
                        colLabel.set(
                            fd.k,
                            fieldDisplayName(fd.k, dictNames) +
                                (fd.u ? `(${fd.u})` : ''),
                        );
                    }
                }
            }
            // 行 params 里模板外的散键也不丢
            for (const r of all) {
                let pv: Record<string, unknown> = {};
                try {
                    pv = JSON.parse(r.params || '{}');
                } catch {
                    /* ignore */
                }
                for (const k of Object.keys(pv)) {
                    if (!colLabel.has(k)) {
                        colOrder.push(k);
                        colLabel.set(k, dictNames.get(k) || k);
                    }
                }
            }

            // 组行下载
            const headers = [
                'ID',
                ...materialHeaders,
                '设备',
                '加工类型',
                '状态',
                '效果标签',
                '更新时间',
                ...colOrder.map((k) => colLabel.get(k) || k),
            ];
            const csvRows = all.map((r) => {
                let pv: Record<string, unknown> = {};
                try {
                    pv = JSON.parse(r.params || '{}');
                } catch {
                    /* ignore */
                }
                return [
                    r.id,
                    ...materialCell(r),
                    r.deviceKey,
                    r.subProcess ? subProcLabel(r.subProcess) : '',
                    STATUS_TEXT[r.status] || r.status,
                    r.resultTag || '',
                    r.updatedAt ? formatTime(r.updatedAt) : '',
                    ...colOrder.map((k) => {
                        const v = pv[k];
                        if (v === null || v === undefined) return '';
                        if (typeof v === 'boolean') return v ? '是' : '否';
                        return v as string | number;
                    }),
                ];
            });
            downloadCsv(
                `params-${archCode}-${dateSuffix()}.csv`,
                headers,
                csvRows,
            );
            onClose();
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setBusy(false);
            setProgress('');
        }
    };

    return (
        <Modal
            open={open}
            width={640}
            title={`导出参数 · ${arch?.label || archCode}`}
            subtitle="先按筛选条件查询命中行, 勾选后导出 (CSV, Excel 可直接打开); 参数值按字段定义拍平成列"
            onClose={onClose}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={f.status}
                            onChange={(v) => patch({ status: v })}
                            placeholder="全部"
                            options={STATUS_SELECT_OPTS}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">机型</label>
                        <SearchSelect
                            className="w-full"
                            value={f.family}
                            onChange={(v) =>
                                patch({ family: v, deviceKey: '' })
                            }
                            placeholder="全部"
                            options={families.map((fa) => ({
                                value: fa,
                                label: fa,
                            }))}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">设备</label>
                        <SearchSelect
                            className="w-full"
                            value={f.deviceKey}
                            onChange={(v) => patch({ deviceKey: v })}
                            placeholder="全部"
                            options={devices
                                .filter(
                                    (d) => !f.family || d.family === f.family,
                                )
                                .map((d) => ({
                                    value: d.deviceKey,
                                    label: deviceLabel(d, lightText),
                                }))}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">加工模式</label>
                        <SearchSelect
                            className="w-full"
                            value={f.processingMode}
                            onChange={(v) => patch({ processingMode: v })}
                            placeholder="全部"
                            options={modeOpts}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">加工类型</label>
                        <SearchSelect
                            className="w-full"
                            value={f.subProcess}
                            onChange={(v) => patch({ subProcess: v })}
                            placeholder="全部"
                            options={subProcessOpts.map((o) => ({
                                value: o.code,
                                label: o.label || o.code,
                            }))}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            {materialHeaders[0]}
                        </label>
                        <input
                            className="inp"
                            placeholder="前缀模糊, 例 PL002"
                            value={f.materialKey}
                            onChange={(e) =>
                                patch({ materialKey: e.target.value })
                            }
                        />
                    </div>
                    {keyKind === 'dual_xcode' && (
                        <div>
                            <label className="field-label">基材</label>
                            <input
                                className="inp"
                                placeholder="精确匹配"
                                value={f.substrateXcode}
                                onChange={(e) =>
                                    patch({ substrateXcode: e.target.value })
                                }
                            />
                        </div>
                    )}
                    <div>
                        <label className="field-label">效果标签</label>
                        <input
                            className="inp"
                            placeholder="例 pending / good"
                            value={f.resultTag}
                            onChange={(e) =>
                                patch({ resultTag: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <label className="field-label">关键词</label>
                        <input
                            className="inp"
                            placeholder="材料键 / 设备 key"
                            value={f.keyword}
                            onChange={(e) => patch({ keyword: e.target.value })}
                        />
                    </div>
                </div>
                <div className="text-[11px] text-slate-400">
                    数据量大时会分页拉取(每页 200 行),请耐心等待;导出列 =
                    关键字段 + 该框架全部参数字段。
                </div>
                {rows && rows.length > 0 && (
                    <CheckPickList
                        items={rows}
                        keyOf={(r) => String(r.id)}
                        searchText={(r) =>
                            [
                                r.id,
                                ...materialCell(r),
                                r.deviceKey,
                                r.subProcess || '',
                                r.subProcess ? subProcLabel(r.subProcess) : '',
                                r.resultTag || '',
                            ].join(' ')
                        }
                        searchPlaceholder="在命中结果内搜索 材料键 / 设备 / 加工类型 / 效果标签"
                        render={(r) => (
                            <>
                                <span className="font-mono text-[11px] text-slate-400 w-12 shrink-0">
                                    {r.id}
                                </span>
                                <span className="font-mono text-xs shrink-0">
                                    {materialCell(r)
                                        .filter(Boolean)
                                        .join(' + ') || '—'}
                                </span>
                                <span className="text-xs text-slate-500 truncate">
                                    {r.deviceKey}
                                    {r.subProcess
                                        ? ` · ${subProcLabel(r.subProcess)}`
                                        : ''}
                                    {r.resultTag ? ` · ${r.resultTag}` : ''}
                                </span>
                                <span className="text-[11px] text-slate-400 ml-auto shrink-0">
                                    {STATUS_TEXT[r.status] || r.status}
                                </span>
                            </>
                        )}
                        selected={selected}
                        onChange={setSelected}
                    />
                )}
                {progress && (
                    <div className="text-sm text-slate-500">{progress}</div>
                )}
                {error && <div className="text-sm text-rose-600">{error}</div>}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500">
                        {rows
                            ? `已选 ${selected.size} / ${rows.length} 行`
                            : '请先按筛选条件查询命中行'}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={onClose}
                            disabled={busy}
                        >
                            取消
                        </button>
                        {rows ? (
                            <>
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    onClick={onQuery}
                                    disabled={busy}
                                    title="按当前筛选条件重新查询"
                                >
                                    重新查询
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={onExport}
                                    disabled={busy || selected.size === 0}
                                    title={
                                        selected.size === 0
                                            ? '请先勾选要导出的行'
                                            : ''
                                    }
                                >
                                    {busy
                                        ? '导出中…'
                                        : `导出已选 ${selected.size} 行`}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={onQuery}
                                disabled={busy}
                            >
                                {busy ? '查询中…' : '查询命中行'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default ParamExportModal;
